import { useState, type FormEvent } from "react";
import type { ServerNode } from "../types";

const emptyForm = {
  name: "",
  hostname: "",
  agentUrl: "http://127.0.0.1:7420",
  agentToken: "",
  role: "node" as "primary" | "node",
  region: "",
  notes: "",
  maxAccounts: 0,
  isDefault: false
};

export function ServersPage({
  servers,
  onCreate,
  onUpdate,
  onDelete,
  onProbe,
  onProbeAll,
  onRefresh
}: {
  servers: ServerNode[];
  onCreate: (data: typeof emptyForm) => Promise<void>;
  onUpdate: (
    id: string,
    data: Partial<{
      hostname: string;
      agentUrl: string;
      agentToken: string;
      region: string;
      notes: string;
      maxAccounts: number;
      isDefault: boolean;
      status: string;
    }>
  ) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onProbe: (id: string) => Promise<void>;
  onProbeAll: () => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof emptyForm, v: string | number | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onCreate(form);
      setShow(false);
      setForm(emptyForm);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Server Nodes</h1>
          <p>
            Advanced · multi-server cluster. Each node runs a RealPanel agent.
            New accounts place on the default node unless you choose otherwise.
          </p>
        </div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-ghost" onClick={() => onRefresh()}>
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onProbeAll()}
          >
            Check all nodes
          </button>
          <button
            type="button"
            className="btn btn-primary btn-inline"
            onClick={() => setShow((v) => !v)}
          >
            {show ? "Close" : "Add node"}
          </button>
        </div>
      </div>

      {show ? (
        <form className="panel-form" onSubmit={submit}>
          <div className="panel-form-title">Register hosting node</div>
          {error ? <div className="error-banner">{error}</div> : null}
          <div className="form-grid">
            <div className="field">
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                pattern="[a-z0-9][a-z0-9._-]{1,31}"
                placeholder="node2"
              />
            </div>
            <div className="field">
              <label>Hostname</label>
              <input
                value={form.hostname}
                onChange={(e) => set("hostname", e.target.value)}
                placeholder="host.example.com"
              />
            </div>
            <div className="field">
              <label>Agent URL</label>
              <input
                value={form.agentUrl}
                onChange={(e) => set("agentUrl", e.target.value)}
                required
                placeholder="http://10.0.0.5:7420"
              />
            </div>
            <div className="field">
              <label>Agent token</label>
              <input
                value={form.agentToken}
                onChange={(e) => set("agentToken", e.target.value)}
                required
                type="password"
                placeholder="shared secret"
              />
            </div>
            <div className="field">
              <label>Region</label>
              <input
                value={form.region}
                onChange={(e) => set("region", e.target.value)}
                placeholder="us-east"
              />
            </div>
            <div className="field">
              <label>Max accounts (0 = unlimited)</label>
              <input
                type="number"
                min={0}
                value={form.maxAccounts}
                onChange={(e) => set("maxAccounts", Number(e.target.value))}
              />
            </div>
            <div className="field checkbox-field">
              <label>
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={(e) => set("isDefault", e.target.checked)}
                />{" "}
                Default placement server
              </label>
            </div>
          </div>
          <button className="btn btn-primary btn-inline" disabled={busy} type="submit">
            {busy ? "Adding…" : "Add node"}
          </button>
        </form>
      ) : null}

      {error && !show ? <div className="error-banner">{error}</div> : null}

      <div className="pkg-grid">
        {servers.map((s) => (
          <section key={s.id} className="card pkg-card">
            <div className="pkg-head">
              <div>
                <div className="card-value" style={{ fontSize: 20 }}>
                  {s.name}
                </div>
                <p className="card-meta mono">{s.agentUrl}</p>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <span className={`badge status-${s.status === "online" ? "active" : s.status === "offline" ? "suspended" : "active"}`}>
                  {s.status}
                </span>
                {s.isDefault ? <span className="badge primary">default</span> : null}
                <span className="badge">{s.role}</span>
              </div>
            </div>
            <dl className="kv compact">
              <dt>hostname</dt>
              <dd className="mono">{s.hostname || "—"}</dd>
              <dt>region</dt>
              <dd>{s.region || "—"}</dd>
              <dt>accounts</dt>
              <dd className="mono">
                {s.accountCount ?? 0}
                {s.maxAccounts > 0 ? ` / ${s.maxAccounts}` : ""}
              </dd>
              <dt>OS</dt>
              <dd className="mono" style={{ fontSize: 12 }}>
                {s.osPretty || "—"}
              </dd>
              <dt>agent</dt>
              <dd className="mono">{s.agentVersion || "—"}</dd>
              <dt>last seen</dt>
              <dd className="mono" style={{ fontSize: 11 }}>
                {s.lastSeenAt
                  ? new Date(s.lastSeenAt).toLocaleString()
                  : "never"}
              </dd>
            </dl>
            {s.lastError ? (
              <div className="error-banner" style={{ marginTop: 10, fontSize: 12 }}>
                {s.lastError}
              </div>
            ) : null}
            <div className="row-actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="link-btn"
                onClick={() => void onProbe(s.id)}
              >
                Probe
              </button>
              {!s.isDefault ? (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => void onUpdate(s.id, { isDefault: true })}
                >
                  Make default
                </button>
              ) : null}
              {s.status !== "disabled" ? (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => void onUpdate(s.id, { status: "disabled" })}
                >
                  Disable
                </button>
              ) : (
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => void onUpdate(s.id, { status: "unknown" })}
                >
                  Enable
                </button>
              )}
              <button
                type="button"
                className="link-btn danger"
                onClick={() => {
                  if (confirm(`Delete server ${s.name}?`)) void onDelete(s.id);
                }}
              >
                Delete
              </button>
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
